import { Box, Button, DropdownList, DropdownListOption, NumberInput, Text } from 'components'
import { CoreCapsuleWithAccount, PoolCapsuleWithAccount } from 'entities'
import { BigNumber, utils } from 'ethers'
import {
  bigDiv,
  bigMul,
  formatBigNumber,
  getFontSize,
  getIconUrlFromGCP,
  getSpacing,
  scaleValue,
} from 'utils'
import { ACTION, ACTION_TYPES, getBorrowAction } from './borrowActions'

const OPTIMAL_BORROW_USAGE = utils.parseEther('0.6')

export interface IBorrowInputProps {
  coreCapsule: CoreCapsuleWithAccount
  poolCapsule: PoolCapsuleWithAccount
  symbol: string
  inputValue: BigNumber
  dropdownOptions: DropdownListOption[]
  dropdownValue: string
  onDropdownChange: (value: DropdownListOption) => void
  onInputChange: (value: BigNumber) => void
  onOptimizeBtnClicked: (optimizedAction: OptimizedAction) => void
  maxValue?: BigNumber
  isMobile?: boolean
  decimals?: number
}

export type OptimizedAction = {
  action: ACTION
  scaledValue: BigNumber
}

export const BorrowInput = ({
  coreCapsule,
  poolCapsule,
  symbol,
  inputValue,
  dropdownOptions,
  dropdownValue,
  onDropdownChange,
  onInputChange,
  onOptimizeBtnClicked,
  isMobile,
  maxValue,
  decimals,
}: IBorrowInputProps) => {
  const {
    accountProperties: { borrowUsage, maxLTV, collateralValue, borrowedAmountScaled },
  } = coreCapsule

  let optimizedAction: OptimizedAction
  const maxLoanScaled = bigMul(maxLTV, collateralValue)
  const targetLoanScaled = bigMul(OPTIMAL_BORROW_USAGE, maxLoanScaled)

  let optimizedText = ''

  if (borrowUsage.lt(OPTIMAL_BORROW_USAGE) && borrowUsage.gte(0)) {
    optimizedAction = {
      action: getBorrowAction(ACTION_TYPES.BORROW),
      scaledValue: targetLoanScaled.sub(borrowedAmountScaled),
    }
    optimizedText = `Borrow $${formatBigNumber(targetLoanScaled.sub(borrowedAmountScaled), 2)}`
  } else if (borrowUsage.gt(OPTIMAL_BORROW_USAGE)) {
    optimizedAction = {
      action: getBorrowAction(ACTION_TYPES.REPAY),
      scaledValue: borrowedAmountScaled.sub(targetLoanScaled),
    }
    optimizedText = `Repay $${formatBigNumber(borrowedAmountScaled.sub(targetLoanScaled), 2)}`
  }

  const borrowAsset = poolCapsule.supportedDepositAssets[0]
  const maxBorrowAmountScaled = scaleValue(
    coreCapsule.getAccountMaxBorrowAmount(poolCapsule, borrowAsset.address),
    borrowAsset.decimals,
  )
  const borrowExceedsMaxAvailable =
    optimizedAction?.action.value === ACTION_TYPES.BORROW &&
    optimizedAction.scaledValue.gt(maxBorrowAmountScaled)

  let isOptimizeBtnHidden =
    optimizedAction &&
    (isWithinTwoPercent(optimizedAction.scaledValue, BigNumber.from(0)) ||
      borrowExceedsMaxAvailable)

  if (
    inputValue &&
    [ACTION_TYPES.BORROW, ACTION_TYPES.REPAY].includes(ACTION_TYPES[dropdownValue]) &&
    !isOptimizeBtnHidden
  ) {
    if (dropdownValue === ACTION_TYPES.BORROW) {
      isOptimizeBtnHidden = isWithinTwoPercent(
        targetLoanScaled,
        borrowedAmountScaled.add(scaleValue(inputValue, decimals)),
      )
    } else {
      isOptimizeBtnHidden = isWithinTwoPercent(
        targetLoanScaled,
        borrowedAmountScaled.sub(scaleValue(inputValue, decimals)),
      )
    }
  }

  return (
    <>
      <Box flexDirection="row">
        <Box flexGrow={1}>
          <DropdownList
            options={dropdownOptions}
            value={dropdownValue}
            onChange={onDropdownChange}
          />
        </Box>
        {!isMobile && !isOptimizeBtnHidden && (
          <Box marginLeft={getSpacing('s')} justifyContent="center">
            <Text size="s" color="TEXT_DARK">
              Optimize
            </Text>
          </Box>
        )}
        {!isOptimizeBtnHidden && optimizedAction && (
          <Box marginLeft={getSpacing('s')} justifyContent="center">
            <Button
              variant="fancy"
              text={optimizedText}
              onClick={() => onOptimizeBtnClicked(optimizedAction)}
              borderWidth="1px"
              style={{
                height: '28px',
                minHeight: 'unset',
                borderRadius: '14px',
                fontSize: getFontSize('xs'),
                minWidth: '116px',
                flexWrap: 'nowrap',
              }}
            />
          </Box>
        )}
      </Box>
      <Box marginTop={getSpacing('s')}>
        <NumberInput
          tokenSymbol={symbol}
          value={inputValue}
          maxValue={maxValue}
          tokenIconUrl={getIconUrlFromGCP(symbol.toLowerCase())}
          onChange={onInputChange}
          decimals={decimals}
          placeholder="Amount"
        />
      </Box>
    </>
  )
}

/**
 * Returns true if both values (18 decimals) are equal at the 2nd decimal place when scaled down
 */
function isWithinTwoPercent(a: BigNumber, b: BigNumber) {
  if (a.isZero() && b.isZero()) {
    return true
  } else if (a.isZero()) {
    return b.lt(utils.parseEther('0.02'))
  } else if (b.isZero()) {
    return a.lt(utils.parseEther('0.02'))
  }

  let result: BigNumber
  if (a.gt(b)) {
    result = bigDiv(a, b)
  } else {
    result = bigDiv(b, a)
  }

  return result.lt(utils.parseEther('1.02'))
}
